import React, { useState, useEffect, useCallback } from "react";
import { Form, Row, Col, Button, Modal, Badge } from "react-bootstrap";
import axios from "axios";
import config from "../../../config";
import { toast } from "react-toastify";
import DataTable from 'react-data-table-component';
import { useHasPermission, PERMISSIONS } from "../../../utils/permissions";
import PermissionGate from "../../common/PermissionGate";

const customStyles = {
  rows: {
    style: {
      minHeight: '80px',
    },
  },
  headCells: {
    style: {
      paddingLeft: '8px',
      paddingRight: '8px',
      backgroundColor: '#f8f9fa',
      fontWeight: 'bold',
    },
  },
  cells: {
    style: {
      paddingLeft: '8px',
      paddingTop: '8px',
      paddingBottom: '8px',
      paddingRight: '8px',
    },
  },
  table: {
    style: {
      minWidth: '100%',
    },
  },
};

export default function PMUnloadingTab({ pmPurchaseOrderId, order, onUpdate }) {
  const [grns, setGrns] = useState([]);
  const [qualityEntries, setQualityEntries] = useState([]);
  const [unloadingEntries, setUnloadingEntries] = useState([]);
  const [showUnloadingModal, setShowUnloadingModal] = useState(false);
  const [warehouses, setWarehouses] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [unloadingForm, setUnloadingForm] = useState({
    grnId: "",
    packingMaterial: "",
    quantity: 0,
    unit: "",
    warehouseLocationId: "",
    remarks: ""
  });

  const canAddUnloading = useHasPermission(PERMISSIONS.UNLOADING_ENTRY_ADD);

  const fetchUnloadingData = useCallback(async () => {
    try {
      const [grnsRes, qualityRes, unloadingRes, warehouseRes] = await Promise.all([
        axios.get(`${config.API_BASE_URL}/pm-grn?pmPurchaseOrder=${pmPurchaseOrderId}`),
        axios.get(`${config.API_BASE_URL}/pm-quality-checks?pmPurchaseOrder=${pmPurchaseOrderId}`),
        axios.get(`${config.API_BASE_URL}/pm-inventory/inward?pmPurchaseOrder=${pmPurchaseOrderId}`),
        axios.get(`${config.API_BASE_URL}/new-warehouses/storage`)
      ]);

      setGrns(grnsRes.data || []);
      setQualityEntries(qualityRes.data || []);
      setUnloadingEntries(unloadingRes.data || []);
      setWarehouses(warehouseRes.data || []);
    } catch (err) {
      console.error("Fetch unloading data error:", err);
    }
  }, [pmPurchaseOrderId]);

  useEffect(() => {
    fetchUnloadingData();
  }, [pmPurchaseOrderId, fetchUnloadingData]);

  const handleUnloadingSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      await axios.post(`${config.API_BASE_URL}/pm-inventory/inward`, {
        pmPurchaseOrder: pmPurchaseOrderId,
        grn: unloadingForm.grnId,
        packingMaterial: unloadingForm.packingMaterial,
        quantity: unloadingForm.quantity,
        unit: unloadingForm.unit,
        warehouseLocation: unloadingForm.warehouseLocationId,
        remarks: unloadingForm.remarks,
        entryDate: new Date().toISOString().split('T')[0]
      });
      toast.success("Stock added to warehouse");
      setUnloadingForm({
        grnId: "",
        packingMaterial: "",
        quantity: 0,
        unit: "",
        warehouseLocationId: "",
        remarks: ""
      });
      setShowUnloadingModal(false);
      fetchUnloadingData();
      if (onUpdate) onUpdate();
    } catch (err) {
      toast.error("Failed to add stock entry");
      console.error(err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const getGRNBadge = (grnId) => {
    const grn = grns.find(g => g._id === grnId || g._id.toString() === grnId);
    return grn ? grn.grnNumber : 'Unknown GRN';
  };

  // Get quality-approved items for unloading
  const getUnloadingItems = () => {
    if (!unloadingForm.grnId) return [];

    return qualityEntries.filter(q =>
      (q.pmGRN === unloadingForm.grnId || q.pmGRN?._id === unloadingForm.grnId) &&
      q.qualityStatus === 'Approved'
    ).map(q => {
      const alreadyUnloaded = unloadingEntries
        .filter(u =>
          (u.grn === unloadingForm.grnId || u.grn?._id === unloadingForm.grnId) &&
          (u.packingMaterial === q.packingMaterial?._id || u.packingMaterial?._id === q.packingMaterial?._id)
        )
        .reduce((sum, u) => sum + (u.quantity || 0), 0);

      const available = q.quantity - alreadyUnloaded;

      return {
        packingMaterial: q.packingMaterial,
        availableQty: available > 0 ? available : 0,
        unit: q.unit,
        qualityCheckedQty: q.quantity
      };
    }).filter(item => item.availableQty > 0);
  };

  const handleUnloadingProductChange = (e) => {
    const packingMaterialId = e.target.value;
    const item = getUnloadingItems().find(i => i.packingMaterial?._id === packingMaterialId);
    setUnloadingForm(prev => ({
      ...prev,
      packingMaterial: packingMaterialId,
      unit: item?.unit || "",
      quantity: item?.availableQty || 0,
      maxQuantity: item?.availableQty || 0,
    }));
  };

  const columns = [
    {
      name: 'GRN',
      selector: row => getGRNBadge(row.grn?._id || row.grn),
      cell: row => <Badge bg="info">{getGRNBadge(row.grn?._id || row.grn)}</Badge>,
      sortable: true,
      width: '140px',
    },
    {
      name: 'Packaging Material',
      selector: row => row.packingMaterial?.name,
      cell: row => (
        <div>
          <strong>{row.packingMaterial?.name || 'N/A'}</strong>
          {row.packingMaterial?.alias && (
            <div className="text-muted small">{row.packingMaterial.alias}</div>
          )}
        </div>
      ),
      sortable: true,
      wrap: true,
      grow: 2,
    },
    {
      name: 'Quantity',
      selector: row => `${row.quantity} ${row.unit}`,
      sortable: true,
      right: true,
      width: '120px',
    },
    {
      name: 'Warehouse Location',
      selector: row => warehouses.find(w => w._id === row.warehouseLocation._id)?.label || 'N/A',
      sortable: true,
      wrap: true,
      grow: 2,
    },
    {
      name: 'Remarks',
      selector: row => row.notes || row.remarks,
      wrap: true,
      grow: 1,
    },
    {
      name: 'Date',
      selector: row => new Date(row.entryDate || row.createdAt).toLocaleDateString('en-IN'),
      sortable: true,
      right: true,
      width: '110px',
    },
  ];

  return (
    <div>
      <DataTable
        columns={columns}
        data={unloadingEntries}
        pagination
        paginationPerPage={10}
        paginationRowsPerPageOptions={[10, 25, 50]}
        customStyles={customStyles}
        striped
        highlightOnHover
        responsive
        subHeader
        dense
        fixedHeader
        fixedHeaderScrollHeight="calc(100vh - 350px)"
        subHeaderComponent={
          <div className="d-flex justify-content-between align-items-center w-100 mb-3">
            <h6 className="mb-0">Stock/Inward Entries</h6>
            <PermissionGate require={PERMISSIONS.UNLOADING_ENTRY_ADD}>
              <Button variant="success" onClick={() => setShowUnloadingModal(true)}>
                <i className="fas fa-plus me-2"></i>Add to Stock
              </Button>
            </PermissionGate>
          </div>
        }
        noDataComponent={
          <div className="text-center py-5 text-muted">
            <i className="fas fa-dolly fa-3x mb-3"></i>
            <h5>No Stock Entries</h5>
            <p>Add approved materials to warehouse stock when they pass quality checks.</p>
          </div>
        }
      />

      {canAddUnloading && (
        <Modal show={showUnloadingModal} onHide={() => setShowUnloadingModal(false)} size="lg">
          <Modal.Header closeButton>
            <Modal.Title>Add to Warehouse Stock</Modal.Title>
          </Modal.Header>
          <Form onSubmit={handleUnloadingSubmit}>
            <Modal.Body>
              <Row className="gy-3">
                <Col md={6}>
                  <Form.Label>GRN *</Form.Label>
                  <Form.Select
                    value={unloadingForm.grnId}
                    onChange={(e) => setUnloadingForm(prev => ({ ...prev, grnId: e.target.value }))}
                    required
                  >
                    <option value="">Select GRN</option>
                    {grns.map(grn => (
                      <option key={grn._id} value={grn._id}>
                        {grn.grnNumber} - {new Date(grn.grnDate).toLocaleDateString()}
                      </option>
                    ))}
                  </Form.Select>
                </Col>
                <Col md={6}>
                  <Form.Label>Packaging Material *</Form.Label>
                  <Form.Select
                    value={unloadingForm.packingMaterial}
                    onChange={handleUnloadingProductChange}
                    required
                    disabled={!unloadingForm.grnId}
                  >
                    <option value="">Select Material</option>
                    {getUnloadingItems().map(item => (
                      <option key={item.packingMaterial?._id} value={item.packingMaterial?._id}>
                        {item.packingMaterial?.name} (Available: {item.availableQty})
                      </option>
                    ))}
                  </Form.Select>
                  {!getUnloadingItems().length && unloadingForm.grnId && (
                    <Form.Text className="text-warning">
                      No quality-approved materials available for this GRN. Please complete quality checks first.
                    </Form.Text>
                  )}
                </Col>
                <Col md={6}>
                  <Form.Label>Quantity *</Form.Label>
                  <div className="input-group">
                    <Form.Control
                      type="number"
                      step="0.01"
                      value={unloadingForm.quantity}
                      min={0.01}
                      max={unloadingForm.maxQuantity || ""}
                      onChange={e => {
                        let val = Number(e.target.value);
                        if (val > (unloadingForm.maxQuantity || 0)) val = unloadingForm.maxQuantity;
                        setUnloadingForm(prev => ({ ...prev, quantity: val }));
                      }}
                      required
                      disabled={!unloadingForm.packingMaterial}
                    />
                    {unloadingForm.unit && (
                      <span className="input-group-text d-none">{unloadingForm.unit}</span>
                    )}
                  </div>
                  <Form.Text className="text-muted">
                    Max available: {unloadingForm.maxQuantity || 0} {unloadingForm.unit}
                  </Form.Text>
                </Col>
                <Col md={6}>
                  <Form.Label>Warehouse Location *</Form.Label>
                  <Form.Select
                    value={unloadingForm.warehouseLocationId}
                    onChange={(e) => setUnloadingForm(prev => ({ ...prev, warehouseLocationId: e.target.value }))}
                    required
                  >
                    <option value="">Select Location</option>
                    {warehouses.map(warehouse => (
                      <option key={warehouse._id} value={warehouse._id}>
                        {warehouse.fullPath}
                      </option>
                    ))}
                  </Form.Select>
                </Col>
                <Col md={12}>
                  <Form.Label>Remarks</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={2}
                    value={unloadingForm.remarks}
                    onChange={(e) => setUnloadingForm(prev => ({ ...prev, remarks: e.target.value }))}
                    placeholder="Additional notes about storage location or handling"
                  />
                </Col>
              </Row>
            </Modal.Body>
            <Modal.Footer>
              <Button variant="secondary" onClick={() => setShowUnloadingModal(false)} disabled={isSubmitting}>
                Cancel
              </Button>
              <Button variant="primary" type="submit" disabled={isSubmitting || !unloadingForm.quantity}>
                {isSubmitting ? 'Adding to Stock...' : 'Add to Stock'}
              </Button>
            </Modal.Footer>
          </Form>
        </Modal>
      )
      }
    </div>
  );
}
